<template>
    <v-dialog
        v-model="$props.params.changePasswordDialog"
        transition="dialog-bottom-transition"
        max-width="600px"
        @keydown.esc="!showProgressBar ? closeChangePasswordDialog() : ''"
        @click:outside="!showProgressBar ? closeChangePasswordDialog() : ''"
        :persistent="showProgressBar"
    >
        <v-card>
            <v-form @submit.prevent="processChangePassword()">

                <v-toolbar
                    color="primary"
                    dark
                    :class="dialogHeadingClasses()"
                >
                    <v-icon v-text="icons.mdiFormTextboxPassword" class="mr-3"></v-icon>
                    {{ $gettext('Change Password') }}
                    <v-spacer></v-spacer>
                    <v-icon class="mr-2" @click.native.stop="closeChangePasswordDialog()" v-text="icons.mdiClose" v-show="!showProgressBar"></v-icon>
                    <!--                    <v-icon @click.native.stop="processChangePassword()" v-text="icons.mdiContentSave" v-show="!showProgressBar"></v-icon>-->
                </v-toolbar>

                <v-divider></v-divider>

                <v-card-text>
                    <v-container class="pt-0">

                        <alert type="info" :text="$gettext('Please note the password is changed immediately, and it does not require to be saved.')" class="mt-0"></alert>

                        <v-row v-if="showProgressBar">
                            <v-col>
                                <progress-bar :text="processingMessage"></progress-bar>
                            </v-col>
                        </v-row>

                        <v-row v-else>
                            <v-col>

                                <v-text-field
                                    :label="$gettext('Current Password')"
                                    required
                                    :placeholder="$gettext('Current Password')"
                                    v-model="currentPassword"
                                    :error-messages="currentPasswordErrors"
                                    outlined
                                    @input="$v.currentPassword.$touch()"
                                    @blur="$v.currentPassword.$touch()"
                                    autofocus
                                    type="password"
                                    clearable
                                ></v-text-field>

                                <v-text-field
                                    :label="$gettext('New Password')"
                                    required
                                    :placeholder="$gettext('New Password')"
                                    v-model="newPassword"
                                    :error-messages="newPasswordErrors"
                                    outlined
                                    @input="$v.newPassword.$touch()"
                                    @blur="$v.newPassword.$touch()"
                                    type="password"
                                    clearable
                                ></v-text-field>

                                <v-text-field
                                    :label="$gettext('Confirm New Password')"
                                    required
                                    :placeholder="$gettext('Confirm New Password')"
                                    v-model="confirmNewPassword"
                                    :error-messages="confirmNewPasswordErrors"
                                    outlined
                                    @input="$v.confirmNewPassword.$touch()"
                                    @blur="$v.confirmNewPassword.$touch()"
                                    type="password"
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>

                <v-card-actions color="secondary" v-show="!showProgressBar">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click.native.stop="closeChangePasswordDialog()">
                        {{ $gettext('Close') }}
                    </v-btn>
                    <v-btn color="primary" type="submit">
                        {{ $gettext('Change Password') }}
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
        <snackbar ref="snackbar"></snackbar>
    </v-dialog>
</template>
<style type="text/scss">
.add-rule-radio-margin .v-input--selection-controls {
    margin-top: 0;
}
</style>

<script>

import Vue from 'vue'
import { find, findIndex, size } from 'lodash'

import Vuelidate from 'vuelidate'
import { required, minLength, sameAs } from 'vuelidate/lib/validators'

import MixinDialog from '@/mixins/MixinDialog'
import MixinIcon from '@/mixins/MixinIcon'
import Snackbar from '@/components/Snackbar'
import MixinStringManipulation from '@/mixins/MixinStringManipulation'
import MixinLodashPadStart from '@/mixins/lodash/MixinLodashPadStart'
import MixinConfig from '@/mixins/database/MixinConfig'
import MixinGenericComponent from '@/mixins/generic/MixinGenericComponent'
import ProgressBar from '@/components/ProgressBar'
import { useConnectionsStore } from '@/stores/ConnectionsStore'
import axios from 'axios'
import MixinChangePassword from '@/mixins/MixinChangePassword'
import Alert from '@/components/Alert'

Vue.use(Vuelidate)

export default {
    components: { Alert, Snackbar, ProgressBar },
    mixins: [MixinConfig, MixinDialog, MixinIcon, MixinStringManipulation, MixinLodashPadStart, MixinGenericComponent, MixinChangePassword],
    props: {
        params: {
            required: true,
            type: Object | Array,
        },
    },
    data () {
        return {
            processingMessage: 'Just a moment...',
            showProgressBar: false,
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        }
    },

    methods: {

        async processChangePassword () {

            let self = this

            self.$v.$touch()
            const appComponent = self.genericComponentGetComponent('App')

            if ( ! self.$v.$invalid) {

                self.showProgressBar = true
                self.processingMessage = 'Working on your request...'

                try {

                    const changePasswordRequest = await self.changePasswordProcessChangePassword(self.currentPassword, self.newPassword)

                    if (changePasswordRequest && changePasswordRequest.hasOwnProperty('status') && changePasswordRequest.status) {
                        appComponent.$refs.snackbar.showSnackbar(self.$gettext('Password updated successfully'), 'success')
                        self.resetForm()
                        self.closeChangePasswordDialog()
                    } else if (changePasswordRequest && changePasswordRequest.hasOwnProperty('error') && changePasswordRequest.error) {
                        appComponent.$refs.snackbar.showSnackbar(changePasswordRequest.error, 'error')
                    } else {
                        appComponent.$refs.snackbar.showSnackbar(self.$gettext('Sorry! An error occurred'), 'error')
                    }

                } catch (e) {

                } finally {
                    self.processingMessage = ''
                    self.showProgressBar = false
                }

            } else {
                appComponent.$refs.snackbar.showSnackbar(self.$gettext('Please resolve the form errors'), 'error')
            }

        },
        closeChangePasswordDialog () {
            let self = this
            let appComponent = self.genericComponentGetComponent('App')
            appComponent.closeDialog('changePasswordDialog')
        },
        resetForm () {
            let self = this
            self.$v.$reset()
            self.currentPassword = ''
            self.newPassword = ''
            self.confirmNewPassword = ''
        },
    },
    computed: {
        currentPasswordErrors () {

            const errors = []
            if ( ! this.$v.currentPassword.$dirty) return errors
            ! this.$v.currentPassword.required && errors.push(this.$gettext('Password is required.'))

            return errors
        },
        newPasswordErrors () {

            const errors = []
            if ( ! this.$v.newPassword.$dirty) return errors
            ! this.$v.newPassword.required && errors.push(this.$gettext('New password is required.'))
            ! this.$v.newPassword.minLength && errors.push(this.$gettext('Please have a more secure password.'))

            return errors
        },
        confirmNewPasswordErrors () {

            const errors = []
            if ( ! this.$v.confirmNewPassword.$dirty) return errors
            ! this.$v.confirmNewPassword.required && errors.push(this.$gettext('New password confirmation is required.'))
            ! this.$v.confirmNewPassword.sameAs && errors.push(this.$gettext('The passwords do not match.'))

            return errors
        },
    },
    validations: {
        currentPassword: {
            required,
        },
        newPassword: {
            required,
            minLength: minLength(5),
        },
        confirmNewPassword: {
            required,
            sameAs: sameAs((vm) => vm.newPassword)
        },
    },
}
</script>
